import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BookmarkIcon, PencilIcon, Trash2Icon, PlayIcon, DatabaseIcon, PlusIcon } from 'lucide-react';
import { useApp, SavedTemplate } from '../context/AppContext';
import { getDataset } from '../data/datasets';
import { ExportButton } from '../components/reports/ExportButton';
import { runReport } from '../utils/reportEngine';
function TemplateCard({
  template,
  index
}: {
  template: SavedTemplate;
  index: number;
}) {
  const {
    deleteTemplate
  } = useApp();
  const navigate = useNavigate();
  const dataset = getDataset(template.config.datasetId);
  const result = runReport(template.config);
  return <motion.article layout initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.97
  }} transition={{
    delay: index * 0.04,
    duration: 0.3,
    ease: 'easeOut'
  }} className="card-float flex flex-col rounded-2xl bg-white p-5" data-id="element-437">
      <div className="flex items-start justify-between gap-3" data-id="element-438">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold-50 text-gold-600" data-id="element-439">
          <BookmarkIcon className="h-4 w-4" data-id="element-440" />
        </span>
        <div className="flex items-center gap-1" data-id="element-441">
          <button type="button" onClick={() => navigate(`/builder?template=${template.id}`)} aria-label={`Edit ${template.name}`} className="flex h-8 w-8 items-center justify-center rounded-full text-navy-300 transition-colors hover:bg-navy-50 hover:text-navy-900" data-id="element-442">
            <PencilIcon className="h-4 w-4" data-id="element-443" />
          </button>
          <button type="button" onClick={() => deleteTemplate(template.id)} aria-label={`Delete ${template.name}`} className="flex h-8 w-8 items-center justify-center rounded-full text-navy-300 transition-colors hover:bg-navy-50 hover:text-rose-600" data-id="element-444">
            <Trash2Icon className="h-4 w-4" data-id="element-445" />
          </button>
        </div>
      </div>
      <h3 className="mt-3 text-sm font-semibold text-navy-900" data-id="element-446">
        {template.name}
      </h3>
      <p className="mt-1 flex-1 text-xs leading-relaxed text-navy-400" data-id="element-447">
        {template.description || 'No description.'}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-navy-300" data-id="element-448">
        <span className="inline-flex items-center gap-1" data-id="element-449">
          <DatabaseIcon className="h-3 w-3" data-id="element-450" />{' '}
          {dataset?.name ?? 'Unknown dataset'}
        </span>
        <span data-id="element-451">Updated {template.updatedAt}</span>
        <span data-id="element-452">by {template.createdBy}</span>
      </div>
      <div className="mt-4 flex items-center gap-2 border-t border-navy-50 pt-4" data-id="element-453">
        <button type="button" onClick={() => navigate(`/builder?template=${template.id}`)} className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full bg-navy-50 text-xs font-medium text-navy-800 transition-colors hover:bg-navy-100" data-id="element-454">
          <PlayIcon className="h-3.5 w-3.5" data-id="element-455" /> Open &amp; Run
        </button>
        <ExportButton reportName={template.name} rowCount={result?.rows.length ?? 0} data-id="element-456" />
      </div>
    </motion.article>;
}
export function Templates() {
  const {
    templates
  } = useApp();
  return <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8" data-id="element-457">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-458">
        <div data-id="element-459">
          <h2 className="text-xl font-semibold tracking-tight text-navy-900" data-id="element-460">
            My Templates
          </h2>
          <p className="mt-1 text-sm text-navy-500" data-id="element-461">
            Custom reports you've saved. Re-run them anytime or open in the
            builder to refine.
          </p>
        </div>
        <Link to="/builder" className="inline-flex h-9 items-center gap-2 self-start rounded-full bg-navy-900 px-4 text-sm font-medium text-white transition-colors hover:bg-navy-800 sm:self-auto" data-id="element-462">
          <PlusIcon className="h-4 w-4" data-id="element-463" /> New Custom Report
        </Link>
      </div>

      {templates.length === 0 ? <div className="card-float rounded-2xl bg-white py-16 text-center" data-id="element-464">
          <BookmarkIcon className="mx-auto h-8 w-8 text-navy-200" data-id="element-465" />
          <p className="mt-3 text-sm font-medium text-navy-900" data-id="element-466">
            No templates yet
          </p>
          <p className="mt-1 text-xs text-navy-400" data-id="element-467">
            Build a custom report and save it as a template to see it here.
          </p>
          <Link to="/builder" className="mt-4 inline-flex h-9 items-center gap-2 rounded-full bg-navy-900 px-4 text-sm font-medium text-white transition-colors hover:bg-navy-800" data-id="element-468">
            <PlusIcon className="h-4 w-4" data-id="element-469" /> Open Report Builder
          </Link>
        </div> : <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-id="element-470">
          <AnimatePresence initial={false} data-id="element-471">
            {templates.map((t, i) => <TemplateCard key={t.id} template={t} index={i} data-id="element-472" />)}
          </AnimatePresence>
        </div>}
    </div>;
}